import React from 'react';
import { Box, Breadcrumbs, Anchor, Text } from '@mantine/core';
import { SubscriptionManager } from '../components/SubscriptionManager';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';

/**
 * Page for managing subscription settings
 */
export function SubscriptionPage() {
  const { isFree, subscriptionStatus, subscriptionTier } = useSubscriptionFeatures();
  
  // Debug logs to check what values we're getting
  console.log('[SubscriptionPage] Subscription status:', { 
    isFree, 
    subscriptionStatus, 
    subscriptionTier 
  });
  
  // Force correct tier permissions if there's a mismatch (like in ReflectionGuidePage)
  const actualIsFree = isFree || subscriptionTier === 'free';
  
  const breadcrumbItems = [
    { title: 'Home', href: '/' },
    { title: 'Subscription', href: '/settings/subscription' },
  ].map((item, index) => (
    <Anchor component={Link} to={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Container with breadcrumbs */}
      <div className="container mx-auto px-4 pt-8 pb-16">
        <Box mb="lg">
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Box>
        
        {/* Header section */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
            {actualIsFree ? (
              <>Choose a <span className="text-[#C2A4D6]">Plan</span></>
            ) : (
              <>Your <span className="text-[#C2A4D6]">Subscription</span> Settings</>
            )}
          </h1>
          {actualIsFree ? (
            <p className="text-lg text-gray-600 font-['Merriweather'] mb-4">
              Upgrade to unlock enhanced features and make the most of your emotional wellness journey
            </p>
          ) : (
            <p className="text-lg text-gray-600 font-['Merriweather'] mb-4">
              Manage your subscription plan and billing details
            </p>
          )}
        </div>
        
        {/* Subscription manager with styled container */}
        <div className="max-w-4xl mx-auto mb-16">
          <SubscriptionManager />
        </div>
        
        {/* FAQ section with updated styling */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 font-['Poppins']">
            Subscription <span className="text-[#C2A4D6]">FAQ</span>
          </h2>
          
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">What's included in each plan?</h3>
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="font-medium text-[#C2A4D6] mb-2">Text Companion</div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Essential emotional support conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Text-based conversations</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Basic emotional awareness building</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">7-day conversation history</span>
                </li>
              </ul>
            </div>
            
            <div className="p-4 bg-[#C2A4D6]/10 rounded-lg">
              <div className="font-medium text-[#C2A4D6] mb-2">Voice Connect</div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Everything in Text Companion</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">AI Voice Connect Messaging</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Advanced emotional awareness building</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Personalized growth insights</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Mood tracking and pattern analysis</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Extended conversation history</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Tailored emotional wellness tips</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">FeelHeard Adaptive Emotional Intelligence</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">How does billing work?</h3>
              <p className="text-gray-600">
                You can choose between monthly or annual billing. Annual billing saves you 20% compared to 
                paying monthly. Your subscription will automatically renew at the end of each billing period.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Can I cancel my subscription?</h3>
              <p className="text-gray-600">
                Yes, you can cancel your subscription at any time. After cancellation, you'll continue 
                to have access to your subscription features until the end of your current billing period.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">What happens to my conversations if I downgrade?</h3>
              <p className="text-gray-600">
                If you downgrade from Voice Connect or Text Companion, you'll still have access to your most recent 
                conversations (up to 7 days). Older conversations will be archived but not deleted, 
                and will become accessible again if you upgrade back to a paid plan.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">How do the different plans compare?</h3>
              <p className="text-gray-600">
                Basic plan provides essential emotional support with core features for everyday well-being. 
                Premium offers enhanced experiences with voice conversations, deeper insights, and unlimited history for 
                comprehensive emotional growth.
              </p>
            </div>
          </div>
        </div>
        
        {/* Satisfaction guarantee */}
        <div className="mt-4 text-center max-w-3xl mx-auto">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Our Satisfaction Guarantee</h3>
            <p className="text-gray-600">
              If you don't feel genuinely supported after your first 5 conversations, 
              we'll refund your subscription. No questions asked. Easy 2-click cancellation anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionPage;